import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Table, Checkbox, Button, Modal } from 'antd';

import {
  colorBarValues,
  getPreferenceLevel,
  getVividnessLevel,
  getFidelityLevel,
} from 'components/libraryTable';
import { Luox } from '..';

import './index.scss';
import { manageSession } from 'utils';

const fullColumnCheckList = [
  'CCT (K)',
  {
    label: (
      <span>
        <i>D</i>
        <sub>uv</sub>
      </span>
    ),
    value: 'Duv',
  },
  {
    label: "u'",
    value: 'uValue',
  },
  {
    label: "v'",
    value: 'vValue',
  },
  {
    label: 'Global Averages',
    value: 'globalAverages',
  },
  {
    label: 'Specification Criteria',
    value: 'specificationCriteria',
  },
  {
    label: 'Local Averages',
    value: 'localAverages',
  },
  {
    label: 'Additional Information',
    value: 'additionalInformation',
  },
  // {
  //   label: (
  //     <span>
  //       <i>R</i>
  //       <sub>f</sub>
  //     </span>
  //   ),
  //   value: "Rf",
  // },
  // {
  //   label: (
  //     <span>
  //       <i>R</i>
  //       <sub>g</sub>
  //     </span>
  //   ),
  //   value: "Rg",
  // },
  // "P Level",
  // "V Level",
  // "F Level",
  {
    label: 'Color Rendition Local Averages',
    value: 'crla',
  },
  {
    label: 'Color Rendition of Color Evaluation Samples (CES)',
    value: 'ces',
  },
  {
    label: 'Normalized Spectral Distribution',
    value: 'nspd',
  },
  // {
  //   label: 'Alpha-Opic Data',
  //   value: 'alphaOpicData',
  // },
];

const defaultColumnCheckList = [
  'CCT (K)',
  'Duv',
  'globalAverages',
  'specificationCriteria',
  'localAverages',
  //   "ces",
  //   "nspd"
  // "luox",
];

const CheckboxGroup = Checkbox.Group;

export const Numerical = ({ reportData }) => {
  const [showChooseColumnsModal, setShowChooseColumnsModal] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(
    defaultColumnCheckList
  );
  const [checkAllColumns, setCheckAllColumns] = useState(false);
  const [formattedLuoxData, setFormattedLuoxData] = useState(null);

  const luoxData = useSelector((state) => state.dataReducers.luoxData);

  const { Numerical_Results, input } = reportData.tm30;

  useEffect(() => {
    const savedColumns = JSON.parse(
      sessionStorage.getItem('session-choices')
    )?.numericalTableColumns;
    if (savedColumns) {
      setColumnVisibility(savedColumns);
    }
  }, []);

  //////////// Color Rendition of Color Evaluation Samples table ////////////

  const cesColumns = [
    {
      title: (
        <span>
          SAMPLE ( <i>i</i>
          {'  '})
        </span>
      ),
      dataIndex: 'samplei',
      key: 'samplei',
      width: 100,
      className: 'ces-second-level',
    },
    {
      title: (
        <span>
          Sample Color Fidelity (<i>R</i>
          <sub>
            f,ces<i>i</i>
          </sub>
          )
        </span>
      ),
      dataIndex: 'scf',
      key: 'scf',
      matchColumnTitle: 'Sample Color Fidelity (Rf,CESi)',
      width: 100,
      className: 'ces-second-level',
    },
    {
      title: 'TEST COORDINATES',
      className: 'ces-second-level',
      // width: 100,
      children: [
        {
          title: (
            <span>
              <i>j</i> '
              <sub>
                t,<i>i</i>
              </sub>
            </span>
          ),
          dataIndex: 'jti',
          key: 'jti',
          matchColumnTitle: 'jti',
          width: 100,
        },
        {
          title: (
            <span>
              <i>a</i>'
              <sub>
                t,<i>i</i>
              </sub>
            </span>
          ),
          dataIndex: 'ati',
          key: 'ati',
          matchColumnTitle: 'ati',
          width: 100,
        },
        {
          title: (
            <span>
              <i>b</i>'
              <sub>
                t,<i>i</i>
              </sub>
            </span>
          ),
          dataIndex: 'bti',
          key: 'bti',
          matchColumnTitle: 'bti',
          width: 100,
        },
      ],
    },
    {
      title: 'REFERENCE COORDINATES',
      className: 'ces-second-level',
      // width: 100,
      children: [
        {
          title: (
            <span>
              <i>j</i> '
              <sub>
                r,<i>i</i>
              </sub>
            </span>
          ),
          dataIndex: 'jri',
          key: 'jri',
          matchColumnTitle: 'jri',
          width: 100,
        },
        {
          title: (
            <span>
              <i>a</i>'
              <sub>
                r,<i>i</i>
              </sub>
            </span>
          ),
          dataIndex: 'ari',
          key: 'ari',
          matchColumnTitle: 'ari',
          width: 100,
        },
        {
          title: (
            <span>
              <i>b</i>'
              <sub>
                r,<i>i</i>
              </sub>
            </span>
          ),
          dataIndex: 'bri',
          key: 'bri',
          matchColumnTitle: 'bri',
          width: 100,
        },
      ],
    },
    {
      title: (
        <span>
          HUE-ANGLE BIN ( <i>j</i> )
        </span>
      ),
      dataIndex: 'jValue',
      key: 'jValue',
      matchColumnTitle: 'jValue',
      width: 100,
      className: 'ces-second-level',
    },
  ];

  const getCesDataSource = () => {
    let cesDataSource = [];
    Numerical_Results.color_sample_fidelity.forEach((value, i) => {
      let cesTableEntry = {
        key: i,
        samplei: `CES${i <= 8 ? '0' : ''}${i + 1}`,
        scf: Math.round(value),
        jti: Numerical_Results.j_test_coordinates[i].toFixed(2),
        ati: Numerical_Results.a_test_coordinates[i].toFixed(2),
        bti: Numerical_Results.b_test_coordinates[i].toFixed(2),
        jri: Numerical_Results.j_ref_coordinates[i].toFixed(2),
        ari: Numerical_Results.a_ref_coordinates[i].toFixed(2),
        bri: Numerical_Results.b_ref_coordinates[i].toFixed(2),
        jValue: Numerical_Results.hue_angle_bin[i],
      };
      cesDataSource.push(cesTableEntry);
    });
    return cesDataSource;
  };

  // Memoizing data source
  const memoizedCesDataSource = useMemo(() => {
    return getCesDataSource();
  }, [Numerical_Results]);

  const cesTable = (
    <Table
      className="ces-table"
      scroll={{ y: 420 }}
      sticky
      pagination={false}
      bordered={true}
      columns={cesColumns}
      dataSource={memoizedCesDataSource}
    />
  );

  //////////// Normalized Spectral Distribution table ////////////

  const spdDataColumns = [
    {
      title: 'Wavelength (nm)',
      dataIndex: 'wavelength',
      key: 'wavelength',
      width: 100,
      className: 'spd-second-level',
    },
    {
      title: 'SPD',
      dataIndex: 'spd',
      key: 'spd',
      width: 100,
      className: 'spd-second-level',
    },
  ];

  const getSpdDataSource = () => {
    let spdDataSource = [];
    input.Wavelength.forEach((value, i) => {
      let cesTableEntry = {
        key: i,
        wavelength: value,
        spd: input.SPD[i],
      };
      spdDataSource.push(cesTableEntry);
    });
    return spdDataSource;
  };

  // Memoizing data source
  const memoizedSpdDataSource = useMemo(() => {
    return getSpdDataSource();
  }, [input]);

  const spdDataTable = (
    <Table
      scroll={{ y: 420 }}
      sticky
      pagination={false}
      bordered={true}
      columns={spdDataColumns}
      dataSource={memoizedSpdDataSource}
    />
  );

  //////////// Numerical view table ////////////

  const columns = [
    {
      title: 'CCT (K)',
      dataIndex: 'cct',
      key: 'cct',
      className: 'small-column',
      width: 60,
    },
    {
      title: (
        <p style={{ marginTop: '10px' }}>
          <i>D</i>
          <sub>uv</sub>
        </p>
      ),
      dataIndex: 'duv',
      key: 'duv',
      matchColumnTitle: 'Duv',
      className: 'small-column',
      width: 60,
    },
    {
      title: "u'",
      dataIndex: 'uValue',
      key: 'uValue',
      matchColumnTitle: 'uValue',
      width: 60,
    },
    {
      title: "v'",
      dataIndex: 'vValue',
      key: 'vValue',
      matchColumnTitle: 'vValue',
      width: 60,
    },
    {
      title: <p className="column-group-headers">Global Averages</p>,
      dataIndex: 'globalAverages',
      key: 'globalAverages',
      matchColumnTitle: 'globalAverages',
      width: 100,
      children: [
        {
          title: (
            <span>
              Fidelity Index (<i>R</i>
              <sub>f</sub>)
            </span>
          ),
          dataIndex: 'rf',
          key: 'rf',
          matchColumnTitle: 'Rf',
          width: 60,
          className: 'small-column',
        },
        {
          title: (
            <span>
              Gamut Index (<i>R</i>
              <sub>g</sub>)
            </span>
          ),
          dataIndex: 'rg',
          key: 'rg',
          matchColumnTitle: 'Rg',
          width: 60,
          className: 'small-column',
        },
      ],
    },
    {
      title: <p className="column-group-headers">Specification Criteria</p>,
      dataIndex: 'specificationCriteria',
      key: 'specificationCriteria',
      matchColumnTitle: 'specificationCriteria',
      width: 100,
      children: [
        {
          title: 'P Level',
          dataIndex: 'preferenceLevel',
          key: 'preferenceLevel',
          width: 60,
          className: 'small-column',
        },
        {
          title: 'V Level',
          dataIndex: 'vividnessLevel',
          key: 'vividnessLevel',
          width: 60,
          className: 'small-column',
        },
        {
          title: 'F Level',
          dataIndex: 'fidelityLevel',
          key: 'fidelityLevel',
          width: 60,
          className: 'small-column',
        },
      ],
    },
    {
      title: <p className="column-group-headers">Local Averages</p>,
      dataIndex: 'localAverages',
      key: 'localAverages',
      matchColumnTitle: 'localAverages',
      width: 100,
      children: [
        {
          title: (
            <span>
              HUE-ANGLE BIN ( <i>j</i> )
            </span>
          ),
          dataIndex: 'habj',
          key: 'habj',
          width: 100,
        },
        {
          title: (
            <span>
              Local Chroma Shift (<i>R</i>
              <sub>cs</sub>
              <sub>
                ,h
                <i>j</i>
              </sub>
              )
            </span>
          ),
          dataIndex: 'lcs',
          key: 'lcs',
          width: 100,
        },
        {
          title: (
            <span>
              Local Hue Shift (<i>R</i>
              <sub>hs</sub>
              <sub>
                ,h
                <i>j</i>
              </sub>
              )
            </span>
          ),
          dataIndex: 'lhs',
          key: 'lhs',
          width: 100,
        },
        {
          title: (
            <span>
              Local Color Fidelity (<i>R</i>
              <sub>f</sub>
              <sub>
                ,h
                <i>j</i>
              </sub>
              )
            </span>
          ),
          dataIndex: 'lcf',
          key: 'lcf',
          width: 100,
        },
      ],
    },
    {
      title: <p className="column-group-headers">Additional Information</p>,
      dataIndex: 'additionalInformation',
      key: 'additionalInformation',
      matchColumnTitle: 'additionalInformation',
      width: 100,
      children: [
        {
          title: (
            <span>
              Sample Count (<i>m</i>)
            </span>
          ),
          dataIndex: 'sampleCount',
          key: 'sampleCount',
          width: 100,
        },
        {
          title: 'TEST COORDINATES',
          width: 100,
          children: [
            {
              title: (
                <span>
                  <i>a</i>'
                  <sub>
                    test,<i>j</i>
                  </sub>
                </span>
              ),
              dataIndex: 'aTestJ',
              key: 'aTestJ',
              width: 100,
            },
            {
              title: (
                <span>
                  <i>b</i>'
                  <sub>
                    test,<i>j</i>
                  </sub>
                </span>
              ),
              dataIndex: 'bTestJ',
              key: 'bTestJ',
              width: 100,
            },
          ],
        },
        {
          title: 'REFERENCE COORDINATES',
          children: [
            {
              title: (
                <span>
                  <i>a</i>'
                  <sub>
                    ref,<i>j</i>
                  </sub>
                </span>
              ),
              dataIndex: 'aRefJ',
              key: 'aRefJ',
              width: 100,
            },
            {
              title: (
                <span>
                  <i>b</i>'
                  <sub>
                    ref,<i>j</i>
                  </sub>
                </span>
              ),
              dataIndex: 'bRefJ',
              key: 'bRefJ',
              width: 100,
            },
          ],
        },
        {
          title: 'COLOR VECTOR GRAPHIC ELEMENTS',
          width: 100,
          children: [
            {
              title: (
                <span>
                  da
                  <sub>relative</sub>
                </span>
              ),
              dataIndex: 'daRelative',
              key: 'daRelative',
              width: 100,
            },
            {
              title: (
                <span>
                  db
                  <sub>relative</sub>
                </span>
              ),
              dataIndex: 'dbRelative',
              key: 'dbRelative',
              width: 100,
            },
            {
              title: (
                <span>
                  path
                  <sub>xRef</sub>
                </span>
              ),
              dataIndex: 'pathXRef',
              key: 'pathXRef',
              width: 100,
            },
            {
              title: (
                <span>
                  path
                  <sub>yRef</sub>
                </span>
              ),
              dataIndex: 'pathYRef',
              key: 'pathYRef',
              width: 100,
            },
            {
              title: (
                <span>
                  path
                  <sub>xTest</sub>
                </span>
              ),
              dataIndex: 'pathXTest',
              key: 'pathXTest',
              width: 100,
            },
            {
              title: (
                <span>
                  path
                  <sub>yTest</sub>
                </span>
              ),
              dataIndex: 'pathYTest',
              key: 'pathYTest',
              width: 100,
            },
          ],
        },
      ],
    },
    {
      title: (
        <p className="column-group-headers">
          Color Rendition of Color Evaluation Samples (CES)
        </p>
      ),
      dataIndex: 'ces',
      key: 'ces',
      matchColumnTitle: 'ces',
      className: 'ces-main-column',
    },
    {
      title: (
        <p className="column-group-headers">Normalized Spectral Distribution</p>
      ),
      dataIndex: 'nspd',
      key: 'nspd',
      matchColumnTitle: 'nspd',
      className: 'spd-main-column',
    },
    // {
    //   title: <p className="column-group-headers">Alpha-Opic Data</p>,
    //   dataIndex: 'alphaOpicData',
    //   key: 'alphaOpicData',
    //   matchColumnTitle: 'alphaOpicData',
    //   className: 'spd-main-column',
    // },
  ];
  const dataSource = [
    {
      key: 1,
      cct: Math.round(Numerical_Results['CCT'][0]),
      duv: Numerical_Results['DUV'][0].toFixed(4),
      uValue: Numerical_Results['u'][0].toFixed(4),
      vValue: Numerical_Results['v'][0].toFixed(4),
      rf: Math.round(Numerical_Results['rf']),
      rg: Math.round(Numerical_Results['rg']),
      preferenceLevel: getPreferenceLevel(Numerical_Results),
      vividnessLevel: getVividnessLevel(Numerical_Results),
      fidelityLevel: getFidelityLevel(Numerical_Results),
      habj: (
        <ul style={{ padding: 0, listStyleType: 'none' }}>
          {colorBarValues.map((color, childIndex) => (
            <li
              key={childIndex}
              style={{
                backgroundColor: color,
                width: '12rem',
                // fontWeight: "bold",
                textAlign: 'end',
                paddingRight: '10px',
              }}
            >
              {childIndex + 1}
            </li>
          ))}
        </ul>
      ),
      lcs: (
        <ul className="no-style-list">
          {Numerical_Results.local_chroma_shift?.map((value, i) => (
            <li key={`${value}-${i}`}>{Math.round(value)}%</li>
          ))}
        </ul>
      ),
      lhs: (
        <ul className="no-style-list">
          {Numerical_Results.local_hue_shift?.map((value, i) => (
            <li key={`${value}-${i}`}>{value.toFixed(2)}</li>
          ))}
        </ul>
      ),
      lcf: (
        <ul className="no-style-list">
          {Numerical_Results.local_color_fidelity?.map((value, i) => (
            <li key={`${value}-${i}`}>{Math.round(value)}</li>
          ))}
        </ul>
      ),
      ces: cesTable,
      sampleCount: (
        <ul className="no-style-list">
          {Numerical_Results.sample_count?.map((value, i) => (
            <li key={`${value}-${i}`}>{Math.round(value)}</li>
          ))}
        </ul>
      ),
      aTestJ: (
        <ul className="no-style-list">
          {Numerical_Results.a_prime_test_j?.map((value, i) => (
            <li key={`${value}-${i}`}>{value.toFixed(2)}</li>
          ))}
        </ul>
      ),
      bTestJ: (
        <ul className="no-style-list">
          {Numerical_Results.b_prime_test_j?.map((value, i) => (
            <li key={`${value}-${i}`}>{value.toFixed(2)}</li>
          ))}
        </ul>
      ),
      aRefJ: (
        <ul className="no-style-list">
          {Numerical_Results.a_prime_ref_j?.map((value, i) => (
            <li key={`${value}-${i}`}>{value.toFixed(2)}</li>
          ))}
        </ul>
      ),
      bRefJ: (
        <ul className="no-style-list">
          {Numerical_Results.b_prime_ref_j?.map((value, i) => (
            <li key={`${value}-${i}`}>{value.toFixed(2)}</li>
          ))}
        </ul>
      ),
      daRelative: (
        <ul className="no-style-list">
          {Numerical_Results.da_relative?.map((value, i) => (
            <li key={`${value}-${i}`}>{value.toFixed(2)}</li>
          ))}
        </ul>
      ),
      dbRelative: (
        <ul className="no-style-list">
          {Numerical_Results.db_relative?.map((value, i) => (
            <li key={`${value}-${i}`}>{value.toFixed(2)}</li>
          ))}
        </ul>
      ),
      pathXRef: (
        <ul className="no-style-list">
          {Numerical_Results.path_xref?.map((value, i) => (
            <li key={`${value}-${i}`}>{value.toFixed(2)}</li>
          ))}
        </ul>
      ),
      pathYRef: (
        <ul className="no-style-list">
          {Numerical_Results.path_yref?.map((value, i) => (
            <li key={`${value}-${i}`}>{value.toFixed(2)}</li>
          ))}
        </ul>
      ),
      pathXTest: (
        <ul className="no-style-list">
          {Numerical_Results.path_xtest?.map((value, i) => (
            <li key={`${value}-${i}`}>{value.toFixed(2)}</li>
          ))}
        </ul>
      ),
      pathYTest: (
        <ul className="no-style-list">
          {Numerical_Results.path_ytest?.map((value, i) => (
            <li key={`${value}-${i}`}>{value.toFixed(2)}</li>
          ))}
        </ul>
      ),
      nspd: spdDataTable,
      // alphaOpicData: luoxData.luoxOutput ? (
      //   <Luox
      //     luoxData={luoxData}
      //     setFormattedLuoxData={setFormattedLuoxData}
      //     measurementName={
      //       reportData.metadata.identifier
      //         ? reportData.metadata.identifier
      //         : 'Measurement'
      //     }
      //     showEditOptions={false}
      //   />
      // ) : (
      //   '-'
      // ),
    },
  ];

  const handleCheckAllColumns = (event) => {
    setCheckAllColumns(event.target.checked);
    let newColumnVisibility = [];
    if (event.target.checked) {
      fullColumnCheckList.forEach((column) => {
        if (typeof column === 'string') {
          newColumnVisibility.push(column);
        } else {
          newColumnVisibility.push(column.value);
        }
      });
      setColumnVisibility(newColumnVisibility);
      manageSession({ numericalTableColumns: newColumnVisibility });
    } else {
      setColumnVisibility(defaultColumnCheckList);
      manageSession({ numericalTableColumns: defaultColumnCheckList });
    }
  };

  //////////// Modal for choosing the columns to be displayed in the table ////////////

  const chooseColumnsModal = (
    <Modal
      title="Choose Columns to Show in Table"
      className="choose-columns-modal"
      visible={showChooseColumnsModal}
      closable
      onOk={() => setShowChooseColumnsModal(false)}
      onCancel={() => setShowChooseColumnsModal(false)}
      footer={[
        <Button key="ok" onClick={() => setShowChooseColumnsModal(false)}>
          OK
        </Button>,
      ]}
    >
      <CheckboxGroup
        options={fullColumnCheckList}
        value={columnVisibility}
        onChange={(checkList) => {
          setColumnVisibility(checkList);
          manageSession({ numericalTableColumns: checkList });
          setCheckAllColumns(
            fullColumnCheckList.length !== checkList.length ? false : true
          );
        }}
      />
      <div className="check-all-columns">
        <Checkbox onChange={handleCheckAllColumns} checked={checkAllColumns}>
          Show all columns
        </Checkbox>
      </div>
    </Modal>
  );

  //////////// Filtered columns ////////////

  let filteredTableColumns = columns?.filter((column) =>
    column.matchColumnTitle
      ? columnVisibility.includes(column.matchColumnTitle)
      : columnVisibility.includes(column.title)
  );

  return (
    <div className="numerical-view-card">
      <Button
        style={{ marginBottom: '10px', marginRight: '10px' }}
        type="primary"
        onClick={() => setShowChooseColumnsModal(true)}
      >
        Show/Hide Columns
      </Button>
      <Table
        scroll={{ x: 500 }}
        // sticky
        pagination={false}
        bordered={true}
        columns={filteredTableColumns}
        dataSource={dataSource}
      />
      {chooseColumnsModal}
    </div>
  );
};
